import { addErrorHandler, navigateToUrl, registerApplication, start, triggerAppChange } from 'single-spa'

import { LicenseInfo } from '@mui/x-license'

import { LoggerFactory, ROOT_LOGGER_CONFIG } from '@cosmica-shell-ui/logger-service'
import type { CosmicAuthenticatedEvent, ShellMFMeta, StaticRemote } from '@cosmica-shell-ui/shell-shared'
import { init, loadRemote } from '@module-federation/enhanced/runtime'
import { KeycloakAuthService } from '@mosaic/iam-authentication'
import { Scope, UserService } from '@mosaic/iam-authorization'
import { formatRoutingPath, getFullItemPath } from '@mosaic/iam-utils'
import { LogLevel, configLogger, logger } from '@mosaic/logger-service'
import type { Route } from '@mosaic/shell-contract'

import { MFEIdentifier, MFEStatusDesc, getFullExposedName } from './core/models/MFERemote'
import { basePaths } from './core/models/basePaths'
import { captureMfeFailure, initSentry, tagRemoteOverrides } from './core/sentry'
import { remoteMap } from './remoteMap'
import type { RemoteOverrideEntry } from './remoteResolver'
import { resolveApplicationRemotes, resolvePlatformRemotes } from './remoteResolver'

const mfes: Record<string, MFEStatusDesc> = {}

/**
 * single-spa app key of a route, namespaced by remote so two products can never
 * collide: `<remote>/<tag>` when the route declares a tag (verbatim, it is the
 * product's stable route id), otherwise `<remote>/<entryModule>` with an ordinal
 * when the same module serves several untagged routes. The single-spa name is
 * the key prefixed with `@`.
 */
const appKeyByRoute = new WeakMap<Route, string>()

function routeAppKey(remote: string, route: Route, ordinal: number): string {
  const routeId = route.tag?.trim() || route.entryModule
  return `${remote}/${routeId}${ordinal > 1 ? `#${ordinal}` : ''}`
}

initSentry()
configLogger({ component: 'ShellUI Host' })

// The key is shared with every remote through the x-license global: products set it only in their standalone entry.
LicenseInfo.setLicenseKey(
  '4d5ac3cd314f04890b8c8dbfcfea294aTz0xMTMwNTUsRT0xNzgwODc2Nzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1pbml0aWFsLEtWPTI='
)
logger.setLevel(LogLevel.INFO)

const MF_META_INFORMATION = 'meta'

function recordMfeStatus(fullExposedName: string, loaded: boolean, message: string, error?: unknown): void {
  loaded ? logger.debug(message) : logger.error(message, { error })
  mfes[fullExposedName] = {
    loaded,
    message,
    error
  }
}

function startSingleSpa(): void {
  // add callback Single-spa for error in lifecycle
  addErrorHandler((err) => {
    const fullExposedName = `${err.appOrParcelName}`.replace(/^@/, '')
    // A load failure has already been recorded and reported by loadRemoteFeature:
    // single-spa re-raises it here as LOAD_ERROR, so only genuinely new failures
    // (bootstrap/mount/unmount) become a Sentry event.
    const alreadyReported = mfes[fullExposedName]?.loaded === false
    recordMfeStatus(fullExposedName, false, `Error loading "${fullExposedName}":`, err)
    if (!alreadyReported) {
      captureMfeFailure(fullExposedName, 'lifecycle', err)
    }
    // re-evaluate activeWhen
    triggerAppChange().catch((rerouteErr) => {
      logger.error('triggerAppChange failed after handling app error', { rerouteErr })
    })
  })

  start({ urlRerouteOnly: true })
}

/**
 * Check if a specific MF (identify by microFeID), with a root path defined, must be enabled from current location path
 * @param microFeID - microfrontend ID
 * @param routePath - root path for this feature MicroFrontend
 * @param locationPath - current location apth
 */
function isActiveForMicroFE(microFeID: string, routePath: string, locationPath: string): boolean {
  // Always a pathname (e.g. /tenant/OU/01/ or /tenant/orders)
  const pathname = locationPath

  // Base pattern: tenant name
  let pattern = '^/(?<tenantName>[a-zA-Z0-9_-]+)'

  // Detect and handle OU section
  const hasOu = routePath.includes('/OU/:ouId')
  if (hasOu) {
    // Allow alphanumeric OU IDs only
    pattern += '/OU/(?<ouId>[a-zA-Z0-9_-]+)'
  }

  // Build static tail: everything after tenant and OU placeholders
  let staticTail = routePath.replace('/:tenantName', '').replace('/OU/:ouId', '').trim()

  // Replace placeholders like :projectId → [^/]+
  staticTail = staticTail.replace(/:[a-zA-Z0-9_]+/g, '[^/]+')

  // Support /* wildcard at the end
  staticTail = staticTail.replace('/*', '(?:/.*)?')

  // Clean redundant slashes
  staticTail = staticTail.replace(/^\/+/, '').replace(/\/+$/, '')

  // Combine
  if (staticTail) pattern += '/' + staticTail

  // Final regex
  const regex = new RegExp(`${pattern}/?$`, 'i')
  const isActive = regex.test(pathname)
  logger.debug(`ACTIVATION - "${microFeID}" for route "${routePath}" with location "${locationPath}"`)
  return isActive
}

/**
 * Check if the current URL matches one of the "basePaths"
 * which require an automatic redirect to /home.
 *
 * Cases handled (from basePaths.ts):
 * 1. /:tenantName          (es. /loom)
 * 2. /:tenantName/x-OU     (es. /loom/x-OU)
 * 3. /:tenantName/OU/:ouId (es. /loom/OU/01)
 */
function shouldRedirectToHome(pathname: string): boolean {
  const tenantPart = '[a-zA-Z0-9_-]+'
  const ouIdPart = '[a-zA-Z0-9_-]+'
  const regex = new RegExp(`^\\/${tenantPart}(\\/x-OU|\\/OU\\/${ouIdPart})?\\/?$`)
  return regex.test(pathname)
}
function pageErrorIsActive(allMetas: Record<string, ShellMFMeta>, locationPath: string): boolean {
  const homePaths = basePaths.map((p) => `${p}/home`)
  const isCoveredByAWorkingRoute =
    homePaths.some((p) => isActiveForMicroFE('pageErrorIsActive', p, locationPath)) ||
    Object.values(allMetas).some((meta) =>
      meta.routes.some((route) => {
        const matches = isActiveForMicroFE('pageErrorIsActive', route.path, locationPath)
        if (!matches) return false
        const appKey = appKeyByRoute.get(route)
        return appKey === undefined || mfes[appKey]?.loaded !== false
      })
    )
  return !isCoveredByAWorkingRoute
}

// Attende che un elemento compaia nel DOM. Serve nel listener shell-ready: i
// metadati risolvono "insieme" al setState della shell-ui, ma la continuation
// della promise (microtask) esegue sempre PRIMA che React committi il render di
// AuthenticatedApp — senza attesa, getElementById del container tornerebbe null
// e single-spa monterebbe i micro-frontend fuori dal layout (in fondo al body).
// Implementato con MutationObserver e NON con requestAnimationFrame: i callback
// rAF non vengono mai eseguiti nei tab in background (es. app aperta con
// cmd+click), dove il bootstrap procede comunque — con rAF la registrazione
// dei micro-frontend resterebbe appesa finché il tab non torna in primo piano.
function waitForElement(id: string): Promise<HTMLElement> {
  return new Promise((resolve) => {
    const existing = document.getElementById(id)
    if (existing) {
      resolve(existing)
      return
    }
    const observer = new MutationObserver(() => {
      const el = document.getElementById(id)
      if (el) {
        observer.disconnect()
        resolve(el)
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
  })
}

/**
 * Load from remote Module Federated a MFE exposed with name that respects the interface {@liink ShellMFMeta}
 *
 * @param moduleFederatedName - Module Federated name (as defined in {@link remoteMap.ts}
 *  and inizialized by {@link moduleFederationInitialization()}) with MFE exposed name (as defined in remote vite.config.js) as standard value './meta'
 */
async function fetchRemoteMeta(moduleFederatedName: string): Promise<ShellMFMeta> {
  const metaModule = { moduleFederatedName, mfExposedName: MF_META_INFORMATION }
  const { getMeta, getI18n } = await loadRemote<any>(getFullExposedName(metaModule))
  const meta = getMeta()
  const [i18nEn, i18nIt] = await Promise.all([getI18n('en'), getI18n('it')])
  meta['i18n'] = { en: i18nEn || {}, it: i18nIt || {} }
  return meta
}

/**
 * Load from remote Module Federated a MFE exposed with name that respects the interface {@liink ShellMFMeta}, with errore handling
 * @param moduleFederatedName - Module Federated name (as defined in {@link remoteMap.ts}
 *  and inizialized by {@link moduleFederationInitialization()}) with MFE exposed name (as defined in remote vite.config.js) as standard value './meta'
 */
async function loadRemoteMeta(moduleFederatedName: string): Promise<ShellMFMeta | void> {
  const metaModule = { moduleFederatedName, mfExposedName: MF_META_INFORMATION }
  const fullExposedName = getFullExposedName(metaModule)
  return await fetchRemoteMeta(moduleFederatedName)
    .then((meta) => {
      recordMfeStatus(fullExposedName, true, `Successfully loaded ${fullExposedName}`)
      return meta
    })
    .catch((err) => {
      // Qui arrivano CORS, 404 ed errori di rete: il remoto viene escluso
      // da menu, rotte e widget; gli altri remoti non sono impattati.
      recordMfeStatus(fullExposedName, false, `Failed to load ${fullExposedName}`, err)
      captureMfeFailure(fullExposedName, 'meta', err)
      return undefined
    })
}

/**
 * Fetch from a Module Federated a MFE exposed
 * @param module - Module Federated name (as defined in {@link remoteMap.ts} and inizialized by {@link moduleFederationInitialization()})
 * with MFE exposed name (as defined in remote vite.config.js)
 */
function fetchRemoteFeature(module: MFEIdentifier): Promise<any> {
  return loadRemote<any>(getFullExposedName(module))
}

/**
 * Load from a Module Federated a MFE exposed, with errore handling
 * @param MFIdentifier - Module Federated name (as defined in {@link remoteMap.ts} and inizialized by {@link moduleFederationInitialization()})
 * with MFE exposed name (as defined in remote vite.config.js)
 */
async function loadRemoteFeature(module: MFEIdentifier, appKey: string): Promise<any> {
  const fullExposedName = getFullExposedName(module)
  logger.debug(`Attempt to load ${fullExposedName} for ${appKey}`)
  return fetchRemoteFeature(module)
    .then((m) => {
      recordMfeStatus(appKey, true, `Successfully loaded ${fullExposedName}`)
      return m
    })
    .catch((err) => {
      // Qui arrivano CORS, 404 ed errori di rete: la route viene esclusa
      // (404); le altre route e gli altri remoti non sono impattati.
      recordMfeStatus(appKey, false, `Failed to load remote ${fullExposedName}`, err)
      captureMfeFailure(appKey, 'load', err)
      throw err
    })
}

/**
 * Inizialize federated module as defined on a {@link remoteMap.ts} and the specific params ovverride
 */
function moduleFederationInitialization(): RemoteOverrideEntry[] {
  const { remotes: resolvedRemoteApp, overrides: appOverrides } = resolveApplicationRemotes(window.location.search)
  const { remotes: resolvedRemotePlatformApp, overrides: platformOverrides } = resolvePlatformRemotes(
    window.location.search
  )

  let remoteApp: Array<any> = resolvedRemoteApp.map((c) => ({
    name: c.name,
    entry: c.entry,
    type: 'module'
  }))
  let remotePlatformApp: Array<any> = resolvedRemotePlatformApp.map((c) => ({
    name: c.name,
    entry: c.entry,
    type: 'module'
  }))
  init({
    name: 'host',
    remotes: [...remoteApp, ...remotePlatformApp]
  })
  logger.info('Module Federated initialized: ', [...remoteApp, ...remotePlatformApp])

  return [...appOverrides, ...platformOverrides]
}

/**
 * Load ALL Meta information about each active remote MFE
 * @param activeRemotes
 */
async function loadAllRemoteMetas(activeRemotes: StaticRemote[]): Promise<Record<string, ShellMFMeta>> {
  // I remoti si caricano in parallelo: ognuno costa una catena di ~5 richieste
  // in serie (remoteEntry -> chunk meta -> i18n), quindi in sequenza il totale è
  // la somma delle latenze di tutti i remoti; in parallelo è il singolo remoto
  // più lento. I fallimenti restano isolati per remoto (vedi loadRemoteMeta).

  const entries = await Promise.all(
    activeRemotes.map(async (remote) => {
      const moduleFederatedName = remote.name
      const meta = await loadRemoteMeta(moduleFederatedName)
      return { moduleFederatedName, meta }
    })
  ).then((loadedRemotes) => {
    logger.info('META Module Federated status: ', mfes)
    return loadedRemotes
  })

  const allMetas: Record<string, ShellMFMeta> = {}
  const existingPaths = new Map<string, string>()
  for (const { moduleFederatedName, meta } of entries) {
    if (!meta) {
      continue
    }
    const metaKey = `${moduleFederatedName}/${MF_META_INFORMATION}`
    try {
      if (!Array.isArray(meta.routes) || !Array.isArray(meta.menu)) {
        const message = `Malformed meta from '${moduleFederatedName}': 'routes' and 'menu' must be arrays. Remote skipped.`
        recordMfeStatus(metaKey, false, message)
        captureMfeFailure(metaKey, 'config', new Error(message))
        continue
      }

      const appKeys = new Set<string>()
      const ordinalByModule = new Map<string, number>()
      const uniqueRoutes = meta.routes.filter((route) => {
        const routePath = route.path
        const candidate = routeAppKey(moduleFederatedName, route, 1)

        // two routes can never share a path: only one app may own an URL
        if (existingPaths.has(routePath)) {
          const clashingMFE = existingPaths.get(routePath)
          const message = `Route clash detected! Path '${routePath}' in '${moduleFederatedName}' is already registered by '${clashingMFE}'.`
          recordMfeStatus(`${candidate}@clash`, false, message)
          captureMfeFailure(candidate, 'config', new Error(message))
          return false
        }

        let appKey: string
        if (route.tag?.trim()) {
          // the tag is the product's stable route id: it must be unique within the remote
          appKey = candidate
          if (appKeys.has(appKey)) {
            const message = `Tag clash detected! Tag '${route.tag}' is used twice in '${moduleFederatedName}'; route '${routePath}' skipped.`
            recordMfeStatus(`${candidate}@clash`, false, message)
            captureMfeFailure(candidate, 'config', new Error(message))
            return false
          }
        } else {
          // untagged routes sharing one exposed module get an ordinal
          const ordinal = (ordinalByModule.get(route.entryModule) ?? 0) + 1
          ordinalByModule.set(route.entryModule, ordinal)
          appKey = routeAppKey(moduleFederatedName, route, ordinal)
        }

        appKeys.add(appKey)
        existingPaths.set(routePath, moduleFederatedName)
        appKeyByRoute.set(route, appKey)
        return true
      })

      allMetas[moduleFederatedName] = { ...meta, routes: uniqueRoutes }
    } catch (err) {
      recordMfeStatus(metaKey, false, `Failed to process meta from '${moduleFederatedName}': remote skipped.`, err)
      captureMfeFailure(metaKey, 'config', err)
    }
  }

  return allMetas
}

function registerCosmicaUI(metasPromise: Promise<Record<string, ShellMFMeta>>, remoteOverrides: RemoteOverrideEntry[]) {
  registerApplication({
    name: '@cosmica-shell-ui/shell-ui',
    app: () => import('@cosmica-shell-ui/shell-ui'),
    activeWhen: () => true,
    customProps: {
      // Promise, non valore: la shell-ui monta (e Keycloak parte) senza aspettare
      // il download dei remoti. La shell-ui la risolve internamente (vedi App.tsx).
      metasPromise: metasPromise.then((allMetas) => Object.values(allMetas).flat()),
      remoteOverrides
    }
  })
}

/**
 * Registers the home dashboard. Only remotes whose meta loaded are handed over:
 * a remote that is down or timed out would otherwise leave its widget slot
 * spinning while the homepage retries loadRemote on its own.
 */
function registerDashboard(loadedRemotes: StaticRemote[]) {
  const homePaths = formatRoutingPath('/home', Scope.OU)

  registerApplication({
    name: '@cosmica-shell-ui/shell-homepage',
    app: () => import('@cosmica-shell-ui/homepage'),
    activeWhen: () => isActiveForMicroFE('shell-homepage', homePaths, location.pathname),
    // Funzione, non oggetto: single-spa la valuta a OGNI mount, così user e token
    // sono quelli correnti e non quelli letti alla registrazione (un'app montata
    // per la prima volta dopo un refresh partirebbe altrimenti con un token
    // già scaduto e ogni sua chiamata API risponderebbe 401 fino al refresh
    // successivo — riprodotto su DEV il 2026-09-23).
    customProps: () => ({
      user: UserService.getUser(),
      token: KeycloakAuthService.getToken() || '',
      remotes: loadedRemotes,
      reportFailure: (widgetModule: string, error: unknown) => captureMfeFailure(widgetModule, 'load', error),
      // monto in un elemento all'interno della shellUI
      domElement: document.getElementById('single-spa-application-container')
    })
  })
}

function registerPageError(allMetas: Record<string, ShellMFMeta>) {
  registerApplication({
    name: '@cosmica-shell-ui/page-error',
    app: () => import('@cosmica-shell-ui/error-app'),
    activeWhen: () => pageErrorIsActive(allMetas, location.pathname),
    customProps: {
      // monto in un elemento all'interno della shellUI
      domElement: document.getElementById('single-spa-application-container')
    }
  })
}

function registerMFModules(meta: ShellMFMeta, moduleFederatedName: string) {
  const productLogger = LoggerFactory.initializeSimple({
    ...ROOT_LOGGER_CONFIG,
    logger: {
      ...ROOT_LOGGER_CONFIG.logger,
      level: LogLevel.DEBUG,
      immutable: true,
      component: moduleFederatedName
    }
  })

  meta?.routes.forEach((route) => {
    const module: MFEIdentifier = { moduleFederatedName, mfExposedName: route.entryModule }
    const appKey = appKeyByRoute.get(route) ?? routeAppKey(moduleFederatedName, route, 1)
    const singleSpaName = `@${appKey}`

    try {
      registerApplication({
        name: singleSpaName,
        // @ts-ignore
        app: () => loadRemoteFeature(module, appKey), //load from remote the specific exposes microFE
        activeWhen: () => isActiveForMicroFE(appKey, route.path, location.pathname),
        // Valutata a ogni mount: token e user correnti (vedi registerDashboard).
        customProps: () => ({
          user: UserService.getUser(),
          token: KeycloakAuthService.getToken() || '',
          logger: productLogger,
          // monto in un elemento all'interno della shellUI
          domElement: document.getElementById('single-spa-application-container')
        })
      })
      recordMfeStatus(appKey, true, `REGISTRY for "${appKey}" with root route "${route.path}"`)
    } catch (err) {
      recordMfeStatus(appKey, false, `Failed REGISTRY for "${appKey}" with root route "${route.path}"`, err)
      captureMfeFailure(appKey, 'registry', err)
    }
  })
}

;(async function bootstrap() {
  try {
    const remoteOverrides = moduleFederationInitialization()
    tagRemoteOverrides(remoteOverrides.map((override) => override.name))

    let activeRemotes = remoteMap.filter((remote) => remote.enabled)

    // 2. AVVIA IL CARICAMENTO DEI METADATI IN BACKGROUND (non bloccante).
    // Prima la shell aspettava qui il download di TUTTI i remoti e solo dopo
    // montava la shell-ui e avviava Keycloak: il redirect all'IdP arrivava a valle
    // di ~13MB scaricati (e buttati via dal redirect). Ora i metadati viaggiano
    // come promise: login e primo render partono subito, i menu/MFE si registrano
    // quando la promise risolve (listener 'shell-ready' sotto, contratto invariato).
    const metasPromise = loadAllRemoteMetas(activeRemotes)

    // 3. REGISTRA SOLO L'UI DELLA SHELL (SEMPRE ATTIVA)
    // È l'unica applicazione che parte subito; riceve i metadati come promise.
    registerCosmicaUI(metasPromise, remoteOverrides)

    // 4. IN ASCOLTO DELL'EVENTO DI AUTENTICAZIONE in arrivo dalla shellUI.
    // shell-ready viene emesso DOPO l'autenticazione, quindi i micro-frontend
    // si registrano sempre con utente e token validi, come prima.
    window.addEventListener('shell-ready', async (event: CosmicAuthenticatedEvent) => {
      logger.info('Shell Ready!')
      if (shouldRedirectToHome(window.location.pathname)) {
        const fullPath = `${getFullItemPath('/home', Scope.OU)}`

        navigateToUrl(fullPath)
      }

      // loadAllRemoteMetas isola già i fallimenti per singolo remoto: qui si
      // arriva solo per errori imprevisti. Registriamo comunque dashboard e
      // page-error per non lasciare la shell senza alcuna applicazione attiva.
      let allMetas: Record<string, ShellMFMeta> = {}
      try {
        allMetas = await metasPromise
      } catch (error) {
        logger.error('Failed to load micro-frontend metas', { error })
        captureMfeFailure('cosmica-shell-ui/host', 'bootstrap', error)
      }

      // Il container è renderizzato da AuthenticatedApp, che monta solo dopo che
      // React ha processato i metadati appena risolti: va atteso, altrimenti i
      // register* qui sotto catturano domElement = null (vedi waitForElement).
      await waitForElement('single-spa-application-container')

      registerDashboard(activeRemotes.filter((remote) => allMetas[remote.name]))
      registerPageError(allMetas)
      for (const remote of activeRemotes) {
        const moduleFederatedName = remote.name
        try {
          registerMFModules(allMetas[moduleFederatedName], moduleFederatedName)
        } catch (err) {
          const metaKey = `${moduleFederatedName}/${MF_META_INFORMATION}`
          recordMfeStatus(metaKey, false, `Failed to register the modules of '${moduleFederatedName}'`, err)
          captureMfeFailure(metaKey, 'registry', err)
        }
      }
    })

    // 5. AVVIA SINGLE-SPA
    // Inizialmente, solo la shell-ui sarà attiva. Le altre verranno caricate
    // dopo l'evento di autenticazione.
    startSingleSpa()
  } catch (error) {
    logger.error('Errore critico durante il bootstrap della Shell:', { error })
    captureMfeFailure('cosmica-shell-ui/host', 'bootstrap', error)
  }
})()
